import React, {useEffect, useMemo, useState} from 'react'
import {Grid, IconButton, ListSubheader, MenuItem, Stack, TextField} from '@mui/material'
//import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import AddIcon from '@mui/icons-material/Add';
import WbTextField from '../../../common/WbTextField'
import WbModal from '../../../common/WbModal'
import WbButton from '../../../common/WbButton'
import WbOutlineButton from '../../../common/WbOutlineButton'
import WbAlert from '../../../common/WbAlert'
import categoriesApi from '../../../../services/api/questionLibrary/categoriesApi'
import {getApiHookParams} from '../../../../auth'

function getTenantContext(survey) {
  const apiParams = getApiHookParams()
  const isMat = apiParams.groupId > 0 && apiParams.schoolId === null
  return {
    matId: isMat ? apiParams.groupId : 0,
    schoolId: isMat ? 0 : (apiParams.schoolId ?? survey?.schoolId ?? 0),
    surveyId: survey?.id ?? null,
  }
}

export default function CategoryPicker({
  survey,
  question,
  updateQuestion,
  disabled,
  onCategoryAssigned,
}) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [savingCategory, setSavingCategory] = useState(false)
  const [categorySearch, setCategorySearch] = useState('')

  const context = useMemo(() => getTenantContext(survey), [survey])

  const loadCategories = () => {
    setLoading(true)
    return categoriesApi
      .getForBuilder({
        matId: context.matId,
        schoolId: context.schoolId,
        surveyId: context.surveyId,
        search: null,
      })
      .then(res => {
        const list = Array.isArray(res) ? res : []
        const sorted = [...list].sort((a, b) => {
          if ((a.sortGroup ?? 9) !== (b.sortGroup ?? 9)) {
            return (a.sortGroup ?? 9) - (b.sortGroup ?? 9)
          }
          return String(a.name ?? '').localeCompare(String(b.name ?? ''))
        })
        setCategories(sorted)
        return sorted
      })
      .catch(() => {
        setCategories([])
        return []
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadCategories()
  }, [context.matId, context.schoolId, context.surveyId])

  const filteredCategories = useMemo(() => {
    const query = categorySearch.trim().toLowerCase()
    if (!query) return categories
    return categories.filter(c => String(c?.name ?? '').toLowerCase().includes(query))
  }, [categories, categorySearch])

  const saveCategory = () => {
    if (!newCategoryName.trim()) return
    setSavingCategory(true)
    categoriesApi
      .create({
        name: newCategoryName.trim(),
        matId: context.matId,
        schoolId: context.schoolId,
        source: null,
        templateId: null,
      })
      .then(async res => {
        const createdName = res?.name || newCategoryName.trim()
        const normalizedCreatedName = createdName.trim().toLowerCase()
        const createdId = res?.id
        const assignFromResponse = createdId
          ? {id: createdId, name: createdName}
          : null

        // Some environments return created=false or omit id for existing categories.
        // Reload and resolve by name so the new/existing category is auto-assigned.
        const categoriesList = await loadCategories()
        const matchedByName = (Array.isArray(categoriesList) ? categoriesList : []).find(
          x => String(x?.name || '').trim().toLowerCase() === normalizedCreatedName
        )
        const assignedCategory = matchedByName || assignFromResponse

        updateQuestion({
          ...question,
          categoryId: assignedCategory?.id ?? null,
          categoryName: assignedCategory?.name ?? createdName,
        })
        if (onCategoryAssigned) {
          await onCategoryAssigned({
            categoryId: assignedCategory?.id ?? null,
            categoryName: assignedCategory?.name ?? createdName,
          })
        }
        WbAlert({
          message: res?.created === false ? 'Category already exists' : 'Category saved',
          type: 'success',
        })
        setShowAddCategory(false)
        setNewCategoryName('')
      })
      .catch(() => {
        WbAlert({message: 'Failed to save category', type: 'error'})
      })
      .finally(() => setSavingCategory(false))
  }

  return (
    <>
      <Grid container spacing={1} alignItems="center" mb={1}>
        <Grid item lg={11} md={11} sm={10} xs={10}>
          <div className="category-selector">
            <WbTextField
              label="Select Category"
              value={question?.categoryId ?? ''}
              select
              disabled={disabled || loading}
              SelectProps={{
                MenuProps: {
                  autoFocus: false,
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#007A7A transparent',
                    },
                  },
                },
                onClose: () => setCategorySearch(''),
              }}
              onChange={e => {
                const selected = categories.find(x => x.id === Number(e.target.value))
                updateQuestion({
                  ...question,
                  categoryId: selected ? selected.id : null,
                  categoryName: selected ? selected.name : null,
                })
              }}
            >
              <ListSubheader>
                <TextField
                  size="small"
                  autoFocus
                  fullWidth
                  placeholder="Search category"
                  value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                />
              </ListSubheader>
              {/* <MenuItem value="">Select category</MenuItem> */}
              {filteredCategories.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
              {!filteredCategories.length && (
                <MenuItem disabled value="__no_results__">
                  No categories found
                </MenuItem>
              )}
            </WbTextField>
          </div>
        </Grid>
        <Grid item lg={1} md={1} sm={2} xs={2} textAlign="right">
          <IconButton
            onClick={() => setShowAddCategory(true)}
            disabled={disabled}
            title="Add category"            
            sx={{
              backgroundColor: '#E84F6B',
              color: '#fff',
              '&:hover': {backgroundColor: '#E82347'},
              width: 38,
              height: 38,
            }}
          >
            <AddIcon sx={{color: '#ffffff'}} />
          </IconButton>
        </Grid>
      </Grid>

      {showAddCategory && (
        <WbModal
          title="Add New Category"
          onClose={() => {
            setShowAddCategory(false)
            setNewCategoryName('')
          }}
          width="35%"
          content={
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12}>
                <WbTextField
                  label="Enter category name"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <WbOutlineButton
                    CustomOutlineButtonText="Cancel"
                    onClick={() => {
                      setShowAddCategory(false)
                      setNewCategoryName('')
                    }}
                    disabled={savingCategory}
                  />
                  <WbButton
                    CustomButtonText="Save"
                    onClick={saveCategory}
                    disabled={savingCategory || !newCategoryName.trim()}
                  />
                </Stack>
              </Grid>
            </Grid>
          }
        />
      )}
    </>
  )
}
